// Temporary stub for daily completion service during Supabase migration
// TODO: Implement with Supabase

import { supabase, supabaseRpc } from '@/lib/supabase';
import { taskService } from './task.service';
import { useAppContextStore } from '@/stores/appContext';
import { TaskRow as Task } from '@/models/supabase/Task';
import { todayStr } from '@/services/date.service';
import { localeService } from '@/services/locale.service';
import { weeklyTargetOf, weekWindowFor, countWeeklyDone } from '@/utils/weeklyTarget';
import { rotationChildrenOf, rotationChildForDate, rotationFrequencyOf } from '@/utils/rotation';
import { isAwayOn } from '@/utils/away';
import { isRecurringOn } from '@/utils/taskRecurrence';
import { getAwayDaysSync } from '@/services/awayDays.service';
import { useAnalytics } from '@/composables/useAnalytics';

const analytics = useAnalytics();

/** Parse a day-semantic value at LOCAL midnight. Accepts 'YYYY-MM-DD' (anchored)
 *  or a full ISO string (used as-is). */
function localMidnight(value: string): Date {
  const v = value.length === 10 ? value + 'T00:00:00' : value;
  return new Date(v);
}

/**
 * Compteur hebdo pour les tâches à objectif hebdomadaire (TW #17573162) :
 * complétions de la semaine locale contenant `date`, par tâche, en EXCLUANT le
 * jour `date` lui-même (une entrée complétée ce jour-là doit rester visible).
 * Retourne null si aucune tâche hebdo — les appelants évitent alors la requête.
 */
async function getWeeklyDoneCounts(childId: string, date: string, tasks: Task[]): Promise<Map<string, number> | null> {
  const weeklyIds = new Set(tasks.filter(t => weeklyTargetOf(t) > 0).map(t => t.id));
  if (weeklyIds.size === 0) return null;
  try {
    const { start, end } = weekWindowFor(date, localeService.getWeekStartIndex());
    const { data, error } = await supabase
      .from('daily_completions')
      .select('date, tasks_data')
      .eq('child_id', childId)
      .gte('date', start)
      .lte('date', end);
    if (error) {
      console.error('[dailyCompletion.getWeeklyDoneCounts] error:', error);
      return null;
    }
    return countWeeklyDone((data ?? []) as any[], weeklyIds, date);
  } catch (err) {
    console.error('[dailyCompletion.getWeeklyDoneCounts] exception:', err);
    return null;
  }
}

/**
 * Vérifie si une tâche est disponible pour une date donnée
 * en tenant compte de start_date, end_date, days_of_week et frequency.
 * `weeklyDone` (issu de getWeeklyDoneCounts) gouverne les tâches à objectif
 * hebdo : disponibles tant que l'objectif n'est pas atteint sur les AUTRES
 * jours de la semaine. Sans compteur fourni (null), elles restent disponibles.
 */
function isTaskAvailableForDate(task: Task, date: string, childId: string, weeklyDone?: Map<string, number> | null): boolean {
  // Jour d'absence déclaré : l'enfant n'est pas au foyer, rien n'est planifié.
  // Ce test unique éteint d'un coup les tâches du jour, la création de la
  // daily_completion (donc tasks_total reste 0) et les rappels de validation
  // qui en dépendent. Voir docs/spec-deux-maisons.md
  //
  // Lecture SYNCHRONE du cache : cette fonction tourne en boucle serrée sur
  // toutes les tâches et sur plusieurs dates. Un enfant pas encore chargé est
  // considéré présent, donc le comportement retombe sur l'historique.
  if (isAwayOn(getAwayDaysSync(childId), date)) {
    return false;
  }

  // Vérifier que la tâche est active
  if (!task.is_active) {
    return false;
  }

  // Vérifier l'assignation : rotation hebdo, sinon sous-ensemble d'enfants
  // (routines multi-enfants), sinon la règle historique all / specific child.
  const rotationChildren = rotationChildrenOf(task);
  const childrenIds = (task as any).children_ids as string[] | null | undefined;
  if (rotationChildren.length > 0) {
    // Rotation (TW #17468676) : seule la semaine de l'enfant compte ; un enfant
    // supprimé de la liste laisse sa semaine sans assignation (v1 assumée).
    const scheduled = rotationChildForDate(
      date,
      (task as any).rotation_anchor_date ?? task.start_date,
      rotationChildren,
      localeService.getWeekStartIndex(),
      rotationFrequencyOf(task)
    );
    if (scheduled !== childId) {
      return false;
    }
  } else if (childrenIds?.length) {
    if (!childrenIds.includes(childId)) {
      return false;
    }
  } else if (task.assignment === 'specific' && task.child_id !== childId) {
    return false;
  }

  // Ne pas compter les tâches bonus dans tasks_total
  if (task.type === 'bonus') {
    return false;
  }

  // Les groupes ne sont jamais planifiés directement : leur présence au jour J
  // est dérivée de leurs membres (voir syncGroupEntries).
  if ((task.type as string) === 'group') {
    return false;
  }

  // Convertir la date en objet Date pour les comparaisons (ancrage minuit local)
  const targetDate = localMidnight(date);
  targetDate.setHours(0, 0, 0, 0);

  // Vérifier start_date
  const startDate = localMidnight(task.start_date);
  startDate.setHours(0, 0, 0, 0);
  if (targetDate < startDate) {
    return false;
  }

  // Vérifier end_date si elle existe
  if (task.end_date) {
    const endDate = localMidnight(task.end_date);
    endDate.setHours(0, 0, 0, 0);
    if (targetDate > endDate) {
      return false;
    }
  }

  // Objectif hebdo : la tâche apparaît chaque jour de la fenêtre tant que le
  // compteur (hors jour évalué) est sous l'objectif ; days_of_week ne s'applique pas.
  const weeklyTarget = weeklyTargetOf(task);
  if (weeklyTarget > 0) {
    if (!weeklyDone) return true;
    return (weeklyDone.get(task.id) ?? 0) < weeklyTarget;
  }

  // Le calendrier : jours, une semaine sur N, Nième jour du mois (#407). La
  // même règle que les séries et le plan de la semaine (utils/taskRecurrence).
  return isRecurringOn(task, date.slice(0, 10), localeService.getWeekStartIndex());
}

/**
 * Returns bonus tasks available for a given date and child.
 * (isTaskAvailableForDate excludes bonus, so we need a separate filter)
 */
/**
 * Returns bonus tasks available for a given child.
 * Bonus tasks are manually assigned per-day by the parent,
 * so we only check active status, assignment and date range — NOT days_of_week.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getBonusTasksForDate(tasks: Task[], date: string, childId: string): Task[] {
  return tasks.filter(t => {
    if (!t.is_active || t.type !== 'bonus') return false;
    if (t.assignment === 'specific' && t.child_id !== childId) return false;
    const targetDate = localMidnight(date); targetDate.setHours(0, 0, 0, 0);
    const startDate = localMidnight(t.start_date); startDate.setHours(0, 0, 0, 0);
    if (targetDate < startDate) return false;
    if (t.end_date) { const endDate = localMidnight(t.end_date); endDate.setHours(0, 0, 0, 0); if (targetDate > endDate) return false; }
    return true;
  });
}


// Define the DailyCompletionTask type
export type DailyCompletionTask = {
  id: string;
  task_id?: string; // Add task_id property
  title: string;
  type: string;
  points: number;
  status: 'pending' | 'completed' | 'skipped';
  deleted_at?: string; // Ajouter l'info de suppression pour l'historique
  icon?: string;
  time_period?: 'morning' | 'afternoon' | 'evening' | null;
  scheduled_time?: string | null;
  /** Entrées type='group' : ids des tâches membres planifiées ce jour. */
  member_task_ids?: string[];
  /** Objectif hebdo (TW #17573162) : N fois par semaine. Absent = tâche planifiée classique. */
  weekly_target?: number;
  /** Rotation hebdo entre enfants (TW #17468676). Absent = pas de rotation. */
  rotation?: boolean;
  /**
   * Le détail que le parent a écrit sous le titre (« ramasser ce qui traîne,
   * ranger ses affaires… »), pour que l'enfant sache ce que la tâche demande
   * (HARMONIA-86). Absent = pas de détail.
   */
  description?: string;
};

/**
 * La description d'une tâche telle que l'instantané la porte : absente quand
 * elle est vide ou faite de blancs, pour ne pas écrire une clé sans contenu
 * dans chaque entrée.
 */
export function descriptionOf(task: { description?: string | null } | null | undefined): string | undefined {
  const raw = task?.description;
  return typeof raw === 'string' && raw.trim() ? raw : undefined;
}

const DAILY_COMPLETION_CACHE_TTL_MS = 30_000 // 30s short TTL for active date reads
const dailyCompletionCache = new Map<string, { data: any; expiry: number; unreconciled?: boolean }>();

// In-flight dedup: plusieurs appelants (App.vue au boot, dashboard par enfant,
// pages tâches) déclenchent getOrCreateDailyCompletion en parallèle avant que
// le cache ne soit rempli — sans ça, chacun refait le fetch ET tente la
// création → course sur la contrainte unique (child_id, date) → 409 en rafale.
const inflightGetOrCreate = new Map<string, Promise<any>>();

function dailyCompletionCacheKey(childId: string, date: string): string {
  return `${childId}:${date}`;
}

export function invalidateDailyCompletionCache(childId: string, date?: string): void {
  if (date) {
    dailyCompletionCache.delete(dailyCompletionCacheKey(childId, date));
  } else {
    for (const key of dailyCompletionCache.keys()) {
      if (key.startsWith(`${childId}:`)) dailyCompletionCache.delete(key);
    }
  }
}

export function invalidateAllDailyCompletionCache(): void {
  dailyCompletionCache.clear();
}

/** Queue: one update at a time per key so rapid validations don't overwrite each other. */
type QueuedJob = { run: () => Promise<void>; resolve: () => void; reject: (err: unknown) => void };
const taskStatusQueue = new Map<string, QueuedJob[]>();
const taskStatusProcessing = new Map<string, boolean>();

function runTaskStatusQueue(key: string, job: () => Promise<void>): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const queue = taskStatusQueue.get(key) ?? [];
    queue.push({ run: job, resolve, reject });
    taskStatusQueue.set(key, queue);
    drainTaskStatusQueue(key);
  });
}

async function drainTaskStatusQueue(key: string): Promise<void> {
  if (taskStatusProcessing.get(key)) return;
  const q = taskStatusQueue.get(key);
  if (!q || q.length === 0) return;

  const entry = q.shift()!;
  if (q.length === 0) taskStatusQueue.delete(key);
  taskStatusProcessing.set(key, true);
  try {
    await entry.run();
    entry.resolve();
  } catch (err) {
    entry.reject(err);
  } finally {
    taskStatusProcessing.set(key, false);
    drainTaskStatusQueue(key);
  }
}

/**
 * Sync title/points/time_period/scheduled_time/icon/weekly_target/rotation from
 * current task definitions into existing tasks_data entries (mutates entries in
 * place). Returns whether ANY of these changed for any entry, computed BEFORE
 * mutation so the caller can tell apart "nothing to persist" from "must write to
 * DB" — sans quoi l'écart snapshot/définition n'est jamais persisté et l'UI
 * re-bascule à chaque chargement (ex. tâche qui saute d'après-midi à soir au
 * refresh). (Review finale I1 — voir dailyCompletion.reconcile-metadata.test.ts.)
 *
 * POINTS — règle à part, et c'est le cœur du garde-fou : on ne recompte JAMAIS
 * rétroactivement. Double verrou — l'appelant doit passer syncPoints (réservé à
 * aujourd'hui et au futur : un jour passé garde son tarif d'époque), et même
 * alors seules les entrées non validées sont retarifées. Une entrée déjà
 * 'completed' garde la valeur réellement gagnée, parce que points_earned
 * alimente le trigger DB sync_child_points_from_completion, qui applique un
 * DELTA sur children.points : réécrire une entrée validée déplacerait le solde
 * de l'enfant pour un travail déjà payé. Une entrée non validée (pending /
 * skipped) ne pèse pas dans points_earned — la resynchroniser ne peut pas
 * réveiller le trigger, et c'est ce qui manquait : le nouveau tarif
 * n'apparaissait ni dans la liste du jour ni au moment de cocher.
 *
 * DESCRIPTION — opt-in elle aussi (syncDescription), et pour la même raison de
 * date. Les instantanés n'en ont jamais porté avant HARMONIA-86 : la synchroniser
 * sur un jour passé ferait passer TOUT l'historique pour « changé » à sa
 * première lecture, et chaque réécriture recalcule points_earned, que le trigger
 * ci-dessus applique en delta. Seuls aujourd'hui et le futur l'affichent.
 */
export function syncTaskMetadataIntoEntries(
  entries: any[],
  taskMap: Map<string, any>,
  opts: { syncPoints?: boolean; syncDescription?: boolean } = {}
): { changed: boolean } {
  // Opt-in : sans le drapeau, aucun tarif n'est retouché. Un jour passé ne peut
  // donc pas être réécrit par mégarde, même si un nouvel appelant apparaît.
  const syncPoints = opts.syncPoints === true;
  const syncDescription = opts.syncDescription === true;
  let changed = false;
  for (const td of entries) {
    const taskId = td.task_id || td.id;
    const task = taskMap.get(taskId);
    if (!task) continue;
    // Détecter les écarts AVANT de muter : `entries` partage ses références avec
    // tasks_data, donc toute comparaison après mutation lit l'objet contre
    // lui-même et ne voit rien. Sans détection ici, l'écart n'est jamais persisté
    // et l'UI re-bascule à CHAQUE chargement (snapshot DB → valeur reconciliée).
    if ((td.time_period || null) !== (task.time_period || null)
      || (td.scheduled_time || null) !== (task.scheduled_time || null)
      || (td.icon || null) !== ((task.icon as string | undefined) || null)) {
      changed = true;
    }
    td.time_period = task.time_period || null;
    td.scheduled_time = task.scheduled_time || null;
    td.icon = (task.icon as string | undefined) || undefined;
    // Titre : le snapshot figeait le nom de création pour toujours, donc une
    // tâche renommée « revenait » à son ancien nom sur les listes du jour à
    // chaque rechargement.
    if (task.title && td.title !== task.title) {
      changed = true;
      td.title = task.title;
    }
    // Points : même symptôme que le titre (la liste du jour et la validation
    // lisent le snapshot, pas la définition), mais réservé aux entrées NON
    // validées — voir la doctrine « pas de recompte rétroactif » en tête de
    // fonction. Les entrées de groupe sont exclues : leur bonus est dérivé par
    // syncGroupEntries, qui a sa propre règle premium.
    if (syncPoints && td.status !== 'completed' && td.type !== 'group' && typeof task.points === 'number') {
      if ((td.points ?? null) !== task.points) {
        changed = true;
        td.points = task.points;
      }
    }
    // Objectif hebdo : resynchroniser le marqueur (même contrainte que ci-dessus —
    // détecter le changement AVANT la mutation, les références sont partagées).
    const newWeeklyTarget = weeklyTargetOf(task) || undefined;
    if ((td.weekly_target ?? undefined) !== newWeeklyTarget) {
      changed = true;
    }
    td.weekly_target = newWeeklyTarget;
    const newRotation = rotationChildrenOf(task).length > 0 || undefined;
    if ((td.rotation ?? undefined) !== newRotation) {
      changed = true;
    }
    td.rotation = newRotation;
    // Description : même contrainte (détecter AVANT de muter), réservée aux
    // jours où l'appelant l'autorise — voir DESCRIPTION en tête de fonction.
    if (syncDescription) {
      const newDescription = descriptionOf(task);
      if ((td.description ?? undefined) !== newDescription) {
        changed = true;
      }
      td.description = newDescription;
    }
  }
  return { changed };
}

/**
 * Statut premium du foyer courant, côté client. Tri-état : null = inconnu
 * (ex. session enfant PIN sans foyer hydraté) — dans ce cas syncGroupEntries
 * PRÉSERVE les points des entrées de groupe existantes au lieu de trancher,
 * pour éviter qu'un appareil sans contexte écrase le bonus (oscillation).
 */
function householdPremiumState(): boolean | null {
  try {
    const household = useAppContextStore().currentHousehold as { is_premium?: boolean } | null;
    if (!household) return null;
    return !!household.is_premium;
  } catch {
    return null;
  }
}

/**
 * Groupes de tâches (Harmonia+) : dérive les entrées type='group' de
 * tasks_data à partir des statuts de leurs membres du jour (mutates entries
 * in place). Un groupe apparaît si au moins un membre est planifié ce jour ;
 * il passe 'completed' quand TOUS ses membres du jour le sont. Ses points
 * (le bonus) transitent par points_earned : le versement et la reprise sont
 * portés par le trigger DB sync_child_points_from_completion (delta), donc
 * symétriques et idempotents par construction.
 *
 * Non-premium : l'entrée reste dérivée (affichage) mais points = 0 (bonus
 * skip silencieux, doctrine allowance). premium=null (inconnu) : les points
 * des entrées existantes sont préservés ; une nouvelle entrée prend les
 * points du groupe.
 *
 * Comme syncTaskMetadataIntoEntries : tout changement est détecté AVANT mutation
 * (entries partage ses références avec tasks_data) et remonté via {changed}
 * pour que l'appelant persiste. Spec : docs/spec-task-groups.md
 */
export function syncGroupEntries(
  entries: any[],
  tasks: Task[],
  opts: { premium: boolean | null }
): { changed: boolean } {
  let changed = false;
  const taskById = new Map(tasks.map(t => [t.id, t]));
  const activeGroups = tasks.filter(t =>
    (t.type as string) === 'group' && t.is_active && !t.deleted_at);
  const activeGroupIds = new Set(activeGroups.map(g => g.id));

  // Appartenance lue côté GROUPE (tasks.member_task_ids) : une tâche partagée
  // par deux enfants peut figurer dans la routine de chacun, ce qu'un lien
  // unique porté par le membre interdisait. Repli sur l'ancien tasks.group_id
  // tant qu'une routine n'a pas de tableau (donnée écrite par une version
  // antérieure de l'app, qui ne connaît que cette colonne).
  const memberIdsByGroup = new Map<string, Set<string>>();
  for (const group of activeGroups) {
    const declared = (group as any).member_task_ids as string[] | null | undefined;
    memberIdsByGroup.set(group.id, new Set(declared
      ?? tasks.filter(t => (t as any).group_id === group.id).map(t => t.id)));
  }

  // Membres présents dans le snapshot du jour, par groupe. Les bonus sont
  // exclus : assignés au jour le jour, ils ne peuvent pas conditionner un
  // bonus de groupe (v1).
  const membersByGroup = new Map<string, any[]>();
  for (const td of entries) {
    if (td.type === 'group' || td.type === 'bonus') continue;
    const taskId = td.task_id || td.id;
    // Sans définition de tâche, l'entrée n'est rattachable à aucune routine
    // (même garde qu'avant : la lecture passait par taskById).
    if (!taskById.has(taskId)) continue;
    for (const groupId of activeGroupIds) {
      if (!memberIdsByGroup.get(groupId)?.has(taskId)) continue;
      const list = membersByGroup.get(groupId) ?? [];
      list.push(td);
      membersByGroup.set(groupId, list);
    }
  }

  // Entrées de groupe orphelines (groupe supprimé/désactivé, ou plus aucun
  // membre planifié ce jour) : retirées.
  for (let i = entries.length - 1; i >= 0; i--) {
    const td = entries[i];
    if (td.type !== 'group') continue;
    const groupId = td.task_id || td.id;
    if (!activeGroupIds.has(groupId) || !membersByGroup.get(groupId)?.length) {
      entries.splice(i, 1);
      changed = true;
    }
  }

  // Créer/synchroniser l'entrée de chaque groupe ayant des membres ce jour.
  for (const group of activeGroups) {
    const members = membersByGroup.get(group.id);
    if (!members?.length) continue;

    const memberIds = members.map(m => m.task_id || m.id).sort();
    const status: 'pending' | 'completed' =
      members.every(m => m.status === 'completed') ? 'completed' : 'pending';

    const existing = entries.find(td =>
      td.type === 'group' && (td.task_id || td.id) === group.id);
    const points = opts.premium === null
      ? (existing ? (existing.points || 0) : (group.points || 0))
      : (opts.premium ? (group.points || 0) : 0);

    if (!existing) {
      entries.push({
        id: group.id,
        task_id: group.id,
        type: 'group',
        title: group.title,
        points,
        status,
        icon: (group as any).icon || undefined,
        member_task_ids: memberIds,
      });
      changed = true;
      continue;
    }

    // Détection AVANT mutation (aliasing avec tasks_data).
    if (existing.status !== status
      || (existing.points || 0) !== points
      || existing.title !== group.title
      || ((existing.icon || null) !== (((group as any).icon as string | undefined) || null))
      || JSON.stringify(existing.member_task_ids ?? null) !== JSON.stringify(memberIds)) {
      changed = true;
    }
    existing.status = status;
    existing.points = points;
    existing.title = group.title;
    existing.icon = (group as any).icon || undefined;
    existing.member_task_ids = memberIds;
  }

  return { changed };
}

/**
 * Reconcile an existing daily_completion with current task definitions.
 * Removes recurring tasks that are no longer scheduled for this date
 * (e.g. days_of_week changed, task deactivated, assignment changed).
 * Bonus tasks are never removed (they are manually assigned per-day).
 * Returns the completion as-is if no changes are needed.
 */
async function reconcileExistingCompletion(completion: any, childId: string): Promise<any> {
  const tasks = await taskService.getChildTasks(childId);
  const date = completion.date;
  const weeklyDone = await getWeeklyDoneCounts(childId, date, tasks);
  const availableTasks = tasks.filter(t => isTaskAvailableForDate(t, date, childId, weeklyDone));

  // Start from existing tasks_data (or empty)
  const existingData: any[] = completion?.tasks_data?.length ? completion.tasks_data : [];
  const taskMap = new Map(tasks.map(t => [t.id, t]));

  // 1. Filter out tasks no longer valid for this date
  const filtered = existingData.filter((td: any) => {
    // Always keep bonus tasks (manually assigned per-day)
    if (td.type === 'bonus') return true;

    // Les entrées de groupe sont dérivées : leur cycle de vie appartient à
    // syncGroupEntries (ci-dessous), pas au filtre de planification.
    if (td.type === 'group') return true;

    const taskId = td.task_id || td.id;
    const task = taskMap.get(taskId);

    // Tasks not in active task list (deleted/disabled):
    // keep for past dates (historical), remove for today (no longer relevant)
    if (!task) {
      const today = todayStr();
      return date < today;
    }

    // Re-check if this recurring task is valid for this date
    return isTaskAvailableForDate(task, date, childId, weeklyDone);
  });

  // 2. Sync time_period/scheduled_time/icon from current task data into existing
  // snapshot entries. `filtered` holds the SAME object references as `existingData`
  // (Array.filter doesn't clone), so this mutates entries shared with existingData —
  // detect the change BEFORE mutating (metadataChanged below would otherwise compare
  // an object to itself and never see the resync). See review finale I1.
  // syncPoints : aujourd'hui et le futur seulement. Un jour passé garde son
  // tarif d'époque — rouvrir points_possible sur une journée archivée
  // réécrirait un bilan que le parent a déjà lu (même doctrine que la
  // re-dérivation des groupes au step 4). syncDescription : même borne, pour
  // ne pas réécrire tout l'historique à sa première lecture (HARMONIA-86).
  const isCurrentOrFuture = date >= todayStr();
  const { changed: metadataSyncChanged } = syncTaskMetadataIntoEntries(filtered, taskMap, {
    syncPoints: isCurrentOrFuture,
    syncDescription: isCurrentOrFuture,
  });

  // 3. Add new tasks that exist but are missing from tasks_data
  const existingTaskIds = new Set(filtered.map((td: any) => td.task_id || td.id));
  for (const task of availableTasks) {
    if (!existingTaskIds.has(task.id)) {
      filtered.push({
        id: task.id,
        task_id: task.id,
        title: task.title,
        type: task.type,
        points: task.points,
        status: 'pending',
        icon: (task as any).icon || undefined,
        description: descriptionOf(task),
        time_period: (task as any).time_period || null,
        scheduled_time: (task as any).scheduled_time || null,
        weekly_target: weeklyTargetOf(task) || undefined,
        rotation: rotationChildrenOf(task).length > 0 || undefined,
      });
    }
  }

  // 4. Groupes : re-dérivation des entrées (après le step 3 pour voir les
  // membres fraîchement ajoutés), UNIQUEMENT pour aujourd'hui et le futur.
  // Les dates passées sont gelées : re-dériver un jour passé après un
  // changement de définition rejouerait des points historiques via le
  // trigger delta sur points_earned.
  let groupSyncChanged = false;
  if (date >= todayStr()) {
    ({ changed: groupSyncChanged } = syncGroupEntries(filtered, tasks, {
      premium: householdPremiumState(),
    }));
  }

  // Check if task list changed. NOTE: `filtered[i]` and `existingData[i]` are the
  // SAME object references for entries that survived step 1 (Array.filter doesn't
  // clone), so any field-level comparison here would read an object against itself.
  // Metadata drift (time_period/scheduled_time/icon) is therefore tracked
  // via metadataSyncChanged, computed BEFORE the mutation in step 2 (idem
  // groupSyncChanged au step 4). This check only catches added entries (step 3)
  // or removed ones (length mismatch).
  const idsUnchanged = filtered.length === existingData.length && filtered.every((td: any, i: number) => (td.task_id || td.id) === (existingData[i]?.task_id || existingData[i]?.id));
  if (idsUnchanged && !metadataSyncChanged && !groupSyncChanged) return completion;

  // Recalculate totals. Les entrées de groupe ne comptent ni dans tasks_total
  // ni dans tasks_completed (ce ne sont pas des tâches à faire) mais leurs
  // points comptent dans points_possible/points_earned (le bonus).
  const tasks_total = filtered.filter((t: any) => t.type !== 'bonus' && t.type !== 'group').length;
  const tasks_completed = filtered.filter((t: any) => t.type !== 'bonus' && t.type !== 'group' && t.status === 'completed').length;
  const bonus_total = filtered.filter((t: any) => t.type === 'bonus').length;
  const bonus_completed = filtered.filter((t: any) => t.type === 'bonus' && t.status === 'completed').length;
  const points_possible = filtered
    .filter((t: any) => t.type !== 'bonus')
    .reduce((sum: number, t: any) => sum + (t.points || 0), 0);
  const points_earned = filtered
    .filter((t: any) => t.status === 'completed')
    .reduce((sum: number, t: any) => sum + (t.points || 0), 0);

  const updated = {
    ...completion,
    tasks_data: filtered,
    tasks_total,
    tasks_completed,
    bonus_total,
    bonus_completed,
    points_possible,
    points_earned,
  };

  // Persist the correction to DB (fire-and-forget to not block reads)
  if (completion.id && !completion.id.startsWith('temp-')) {
    supabase
      .from('daily_completions')
      .update({
        tasks_data: filtered,
        tasks_total,
        tasks_completed,
        bonus_total,
        bonus_completed,
        points_possible,
        points_earned,
        updated_at: new Date().toISOString(),
      })
      .eq('id', completion.id)
      .then(
        () => invalidateDailyCompletionCache(childId, date),
        (err: unknown) => console.error('Error persisting reconciliation:', err)
      );
  }

  return updated;
}

/**
 * Points des entrées de groupe d'une journée. Le bonus d'une routine compte
 * dans points_possible : c'est la règle de reconcileExistingCompletion, et
 * toute autre formule doit la suivre. Les corrections de totaux l'oubliaient,
 * si bien que les deux se réécrivaient la même ligne à tour de rôle dans les
 * foyers à routines, une écriture et une vague de relectures par validation
 * (HARMONIA-77).
 */
function groupPointsOf(tasksData: unknown): number {
  if (!Array.isArray(tasksData)) return 0;
  return tasksData
    .filter((t: any) => t?.type === 'group')
    .reduce((sum: number, t: any) => sum + (t.points || 0), 0);
}

/** Persist tasks_total/points_possible correction for a (child, date). Call from task mutations or server job; never from read path. */
export async function persistDailyCompletionTotalsCorrection(childId: string, date: string): Promise<void> {
  const tasks = await taskService.getChildTasks(childId);
  const weeklyDone = await getWeeklyDoneCounts(childId, date, tasks);
  const availableTasks = tasks.filter((t) => isTaskAvailableForDate(t, date, childId, weeklyDone));
  const tasks_total = availableTasks.length;
  const { data } = await supabase
    .from('daily_completions')
    .select('id, tasks_data')
    .eq('child_id', childId)
    .eq('date', date)
    .maybeSingle();
  const points_possible = availableTasks.reduce((sum, t) => sum + (t.points || 0), 0) + groupPointsOf(data?.tasks_data);
  if (data?.id && !data.id.startsWith('temp-')) {
    await supabase
      .from('daily_completions')
      .update({ tasks_total, points_possible, updated_at: new Date().toISOString() })
      .eq('id', data.id);
    invalidateDailyCompletionCache(childId, date);
  }
}

/**
 * Remove a task from today's daily_completion (tasks_data) and recalculate totals.
 * Called when a task is deleted or deactivated so it stops appearing in the child's day.
 */
export async function removeTaskFromDailyCompletion(taskId: string, childIds: string[]): Promise<void> {
  const today = todayStr();
  for (const childId of childIds) {
    try {
      const { data: dc } = await supabase
        .from('daily_completions')
        .select('id, tasks_data, tasks_completed, points_earned')
        .eq('child_id', childId)
        .eq('date', today)
        .maybeSingle();
      if (!dc || !dc.tasks_data || dc.id.startsWith('temp-')) continue;

      const tasksData = dc.tasks_data as any[];
      const originalLength = tasksData.length;
      const filtered = tasksData.filter(
        (t: any) => t.id !== taskId && t.task_id !== taskId
      );
      // Nothing to remove
      if (filtered.length === originalLength) continue;

      const tasks_total = filtered.filter((t: any) => t.type === 'recurring').length;
      const tasks_completed = filtered.filter((t: any) => t.type === 'recurring' && t.status === 'completed').length;
      const bonus_total = filtered.filter((t: any) => t.type === 'bonus').length;
      const bonus_completed = filtered.filter((t: any) => t.type === 'bonus' && t.status === 'completed').length;
      const points_possible = filtered
        .filter((t: any) => t.type === 'recurring')
        .reduce((sum: number, t: any) => sum + (t.points || 0), 0);
      const points_earned = filtered
        .filter((t: any) => t.status === 'completed')
        .reduce((sum: number, t: any) => sum + (t.points || 0), 0);

      await supabase
        .from('daily_completions')
        .update({
          tasks_data: filtered,
          tasks_total,
          tasks_completed,
          bonus_total,
          bonus_completed,
          points_possible,
          points_earned,
          updated_at: new Date().toISOString(),
        })
        .eq('id', dc.id);

      invalidateDailyCompletionCache(childId, today);
    } catch (err) {
      console.error(`Error removing task ${taskId} from daily completion for child ${childId}:`, err);
    }
  }
}

export const dailyCompletionService = {
  // Get or create a daily completion (short TTL cache to reduce redundant calls)
  async getOrCreateDailyCompletion(childId: string, date: string, providedTasks?: any[]): Promise<any> {
    const key = dailyCompletionCacheKey(childId, date);
    const now = Date.now();

    const cached = dailyCompletionCache.get(key);
    if (cached && now < cached.expiry) {
      // The batch loader (getDailyCompletionsForChildren) warms this cache with
      // RAW rows that have NOT been reconciled. Serving those directly would
      // shadow reconciliation — e.g. a task the parent just deactivated would
      // keep showing on the "all kids" view. Reconcile the cached row once, then
      // re-cache the clean result so subsequent reads stay instant.
      if (cached.unreconciled) {
        const reconciled = await reconcileExistingCompletion(cached.data, childId);
        const copy = { ...reconciled, tasks_data: reconciled.tasks_data ? [...reconciled.tasks_data] : [] };
        dailyCompletionCache.set(key, { data: copy, expiry: Date.now() + DAILY_COMPLETION_CACHE_TTL_MS });
        return reconciled;
      }
      // Return a shallow copy so callers don't mutate cached data
      return { ...cached.data, tasks_data: cached.data.tasks_data ? [...cached.data.tasks_data] : [] };
    }

    // Dedup: si un fetch/create est déjà en vol pour cette clé, s'y raccrocher
    // au lieu de relancer le même travail (et la même création) en parallèle.
    const inflight = inflightGetOrCreate.get(key);
    if (inflight) {
      const data = await inflight;
      // Copie par appelant : mêmes garanties d'isolation que le chemin cache
      return { ...data, tasks_data: data.tasks_data ? [...data.tasks_data] : [] };
    }

    const promise = this._fetchOrCreateDailyCompletion(childId, date, providedTasks)
      .finally(() => { inflightGetOrCreate.delete(key); });
    inflightGetOrCreate.set(key, promise);
    return promise;
  },

  // Network path of getOrCreateDailyCompletion (cache/dedup handled by the caller)
  async _fetchOrCreateDailyCompletion(childId: string, date: string, providedTasks?: any[]): Promise<any> {
    const key = dailyCompletionCacheKey(childId, date);

    const setCache = (data: any) => {
      // Deep-copy tasks_data so callers mutating .tasks_data don't corrupt the cache
      const cached = { ...data, tasks_data: data.tasks_data ? [...data.tasks_data] : [] };
      dailyCompletionCache.set(key, { data: cached, expiry: Date.now() + DAILY_COMPLETION_CACHE_TTL_MS });
      return data;
    };

    try {
      // First try using RPC to avoid ambiguous column references
      let rowKnownAbsent = false;
      try {
        const { data: rpcData, error: rpcError } = await supabaseRpc.rpc(
          'get_daily_completion',
          {
            p_child_id: childId,
            p_date: date
          }
        );

        if (!rpcError && rpcData && rpcData.length > 0) {
          // daily_completion exists — reconcile to remove tasks whose schedule changed
          const reconciled = await reconcileExistingCompletion(rpcData[0], childId);
          return setCache(reconciled);
        }

        if (!rpcError && rpcData && rpcData.length === 0) {
          // Le RPC a répondu : la ligne n'existe pas. Inutile de refaire un
          // GET direct pour la même absence — on passe droit à la création.
          rowKnownAbsent = true;
        }

        if (rpcError && rpcError.message && !rpcError.message.includes('function does not exist')) {
          console.error('RPC error:', rpcError);
        }

        // If RPC doesn't exist or fails, fall back to direct query
      } catch (rpcError) {
        console.error('Error trying RPC method:', rpcError);
        // Continue to direct query fallback
      }

      if (!rowKnownAbsent) {
        // Check if a completion already exists for this child on this date.
        // maybeSingle: 0 ligne est un cas NORMAL (premier accès du jour) —
        // .single() renverrait un 406 loggé en erreur réseau par le navigateur.
        const { data: existingCompletion, error: fetchError } = await supabase
          .from('daily_completions')
          .select('id, child_id, date, tasks_total, tasks_completed, bonus_total, bonus_completed, points_possible, points_earned, tasks_data, created_at, updated_at')
          .eq('child_id', childId)
          .eq('date', date)
          .maybeSingle();

        // daily_completion exists — reconcile to remove tasks whose schedule changed
        if (existingCompletion) {
          const reconciled = await reconcileExistingCompletion(existingCompletion, childId);
          return setCache(reconciled);
        }

        if (fetchError) {
          console.error('Error fetching daily completion:', fetchError);
          throw fetchError;
        }
      }

      // Use provided tasks or fetch them if not provided
      const tasks = providedTasks || await taskService.getChildTasks(childId);

      // Filtrer les tâches disponibles pour cette date spécifique
      // en tenant compte de start_date, end_date, days_of_week et frequency
      const weeklyDone = await getWeeklyDoneCounts(childId, date, tasks);
      const availableTasks = tasks.filter(t => isTaskAvailableForDate(t, date, childId, weeklyDone));

      const initialTasksData: any[] = availableTasks.map(task => ({
        id: task.id,
        task_id: task.id,
        title: task.title,
        type: task.type,
        points: task.points || 0,
        status: 'pending',
        icon: (task as any).icon || undefined,
        description: descriptionOf(task),
        time_period: (task as any).time_period || null,
        scheduled_time: (task as any).scheduled_time || null,
        weekly_target: weeklyTargetOf(task) || undefined,
        rotation: rotationChildrenOf(task).length > 0 || undefined,
      }));

      // Groupes : dériver les entrées dès la création du snapshot (statuts
      // tous pending, le bonus compte dans points_possible).
      syncGroupEntries(initialTasksData, tasks, { premium: householdPremiumState() });

      // Create a new completion object
      const newCompletion = {
        child_id: childId,
        date: date,
        tasks_total: availableTasks.length,
        tasks_completed: 0,
        bonus_total: 0, // Start with 0 bonus tasks - they'll be added manually
        bonus_completed: 0,
        points_possible: initialTasksData
          .filter(td => td.type !== 'bonus')
          .reduce((sum, td) => sum + (td.points || 0), 0),
        points_earned: 0,
        tasks_data: initialTasksData
      };

      // Create a temporary object to store data in case of failure
      const tempCompletion = {
        id: 'temp-' + Date.now(),
        ...newCompletion,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      try {
        // Appel direct du RPC de création — l'ancienne « probe » préalable
        // (create_daily_completion avec un UUID factice) générait un 409
        // garanti dans la console à chaque passage. Si la fonction n'existe
        // pas, l'erreur du vrai appel nous le dit et on retombe sur l'insert.
        const { data: rpcData, error: rpcError } = await supabaseRpc.rpc(
          'create_daily_completion',
          {
            p_child_id: childId,
            p_date: date,
            p_tasks_total: newCompletion.tasks_total,
            p_tasks_completed: newCompletion.tasks_completed,
            p_bonus_total: newCompletion.bonus_total,
            p_bonus_completed: newCompletion.bonus_completed,
            p_points_possible: newCompletion.points_possible,
            p_points_earned: newCompletion.points_earned,
            p_tasks_data: newCompletion.tasks_data
          }
        );

        if (!rpcError && rpcData) {
          return setCache(rpcData[0]);
        }

        if (rpcError) {
          if (rpcError.code === '23505' && rpcError.message.includes('daily_completions_child_id_date_key')) {
            // Un autre appelant a créé la ligne entre-temps — la relire
            const { data: existingData, error: fetchError } = await supabase
              .from('daily_completions')
              .select('id, child_id, date, tasks_total, tasks_completed, bonus_total, bonus_completed, points_possible, points_earned, tasks_data, created_at, updated_at')
              .eq('child_id', childId)
              .eq('date', date)
              .maybeSingle();

            if (fetchError || !existingData) {
              console.error('Error fetching existing daily completion:', fetchError);
              return tempCompletion;
            }
            return setCache(existingData);
          }

          if (!(rpcError.message && rpcError.message.includes('function does not exist'))) {
            console.error('RPC function failed:', rpcError);
          }
        }

        // Try direct insert as fallback or if RPC doesn't exist
        const { data, error } = await supabase
          .from('daily_completions')
          .insert(newCompletion)
          .select('id, child_id, date, tasks_total, tasks_completed, bonus_total, bonus_completed, points_possible, points_earned, tasks_data, created_at, updated_at')
          .single();

        if (error) {
          if (error.code === '23505' && error.message.includes('daily_completions_child_id_date_key')) {
            const { data: existingData, error: fetchError } = await supabase
              .from('daily_completions')
              .select('id, child_id, date, tasks_total, tasks_completed, bonus_total, bonus_completed, points_possible, points_earned, tasks_data, created_at, updated_at')
              .eq('child_id', childId)
              .eq('date', date)
              .maybeSingle();

            if (fetchError || !existingData) {
              console.error('Error fetching existing daily completion:', fetchError);
              return tempCompletion;
            }
            return setCache(existingData);
          }

          console.error('Error inserting daily completion:', error);
          return tempCompletion;
        }

        return setCache(data);
      } catch (insertError) {
        console.error('Exception during daily completion insert:', insertError);
        // Return a temporary object in case of error
        return tempCompletion;
      }
    } catch (error) {
      console.error('Error in getOrCreateDailyCompletion:', error);
      // Return a default object in case of error
      return {
        id: 'temp-completion-id',
        child_id: childId,
        date: date,
        tasks_total: 0,
        tasks_completed: 0,
        bonus_total: 0,
        bonus_completed: 0,
        points_possible: 0,
        points_earned: 0,
        tasks_data: [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
    }
  },

  /**
   * Batch read: fetch existing daily_completions for many children on a single date
   * in ONE query. Children without an existing row are simply absent from the result —
   * the caller decides whether to create them lazily (e.g. on first interaction) via
   * getOrCreateDailyCompletion.
   *
   * Hydrates the per-(child,date) cache so subsequent single reads are instant.
   * Does NOT reconcile (keep the read path cheap). Reconciliation still happens on
   * single-row reads via getOrCreateDailyCompletion when needed.
   */
  async getDailyCompletionsForChildren(childIds: string[], date: string): Promise<Map<string, any>> {
    const out = new Map<string, any>();
    if (!childIds.length) return out;

    try {
      const { data, error } = await supabase
        .from('daily_completions')
        .select('id, child_id, date, tasks_total, tasks_completed, bonus_total, bonus_completed, points_possible, points_earned, tasks_data, created_at, updated_at')
        .in('child_id', childIds)
        .eq('date', date);

      if (error) {
        console.error('[dailyCompletion.getDailyCompletionsForChildren] error:', error);
        return out;
      }

      const now = Date.now();
      for (const row of data || []) {
        out.set(row.child_id, row);
        // Hydrate the short-TTL cache so a subsequent getOrCreateDailyCompletion
        // for the same key returns immediately without another round-trip.
        const key = dailyCompletionCacheKey(row.child_id, date);
        const cached = { ...row, tasks_data: Array.isArray(row.tasks_data) ? [...row.tasks_data] : [] };
        // Flag the entry as unreconciled: this is a raw DB row, so a later
        // getOrCreateDailyCompletion must reconcile it (drop deactivated/expired
        // tasks) instead of serving it as-is.
        dailyCompletionCache.set(key, { data: cached, expiry: now + DAILY_COMPLETION_CACHE_TTL_MS, unreconciled: true });
      }
    } catch (err) {
      console.error('[dailyCompletion.getDailyCompletionsForChildren] exception:', err);
    }

    return out;
  },

  // Get daily completions for a child
  async getDailyCompletions(childId: string, startDate: string, endDate?: string): Promise<any[]> {
    try {
      let query = supabase
        .from('daily_completions')
        .select()  // Remove the '*' to use default selection without ambiguity
        .eq('child_id', childId);

      if (startDate && endDate) {
        query = query.gte('date', startDate).lte('date', endDate);
      } else if (startDate) {
        query = query.eq('date', startDate);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching daily completions:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getDailyCompletions:', error);
      return [];
    }
  },

  // Queue: one update at a time per (childId, date) to avoid race conditions when validating multiple tasks quickly
  /**
   * `surface` dit d'où vient le geste, pour Amplitude : la carte d'un enfant
   * (`task_card`, par défaut) ou la journée famille de l'Accueil
   * (`home_family`). Sans elle, les validations de l'ancien tableau de bord
   * étaient indiscernables et on n'a jamais pu dire s'il servait.
   */
  async updateTaskStatus(childId: string, date: string, taskId: string, status: string, surface = 'task_card'): Promise<void> {
    const key = `${childId}:${date}`;
    return runTaskStatusQueue(key, () => this._updateTaskStatusOne(childId, date, taskId, status, surface));
  },

  async _updateTaskStatusOne(childId: string, date: string, taskId: string, status: string, surface = 'task_card'): Promise<void> {
    try {
      // Use getDailyCompletion (no reconciliation) to get the current DB state.
      // getOrCreateDailyCompletion would reconcile and could reset statuses of tasks
      // that were just updated optimistically but not yet persisted.
      let completion = await this.getDailyCompletion(childId, date);
      if (!completion) {
        completion = await this.getOrCreateDailyCompletion(childId, date);
      }

      if (!completion || !completion.tasks_data) {
        throw new Error('No completion found or tasks_data is missing');
      }

      // Update the task status in tasks_data
      const tasksData = [...(completion.tasks_data || [])];

      // Check for both task_id and id fields to handle different formats
      const taskIndex = tasksData.findIndex((t: any) =>
        t.id === taskId || t.task_id === taskId
      );

      // Capture the original status before modification (for coin deduction logic)
      const originalTaskStatus = taskIndex !== -1 ? tasksData[taskIndex].status : null;

      // L'enfant avait-il soumis cette tâche ? À lire AVANT l'écriture : le
      // trigger resolve_submissions_from_completion approuve la soumission en
      // attente dans la transaction qui écrit la journée, et le PATCH du client
      // qui servait à le savoir ne trouve alors plus aucune ligne `pending`.
      // `had_submission` valait donc toujours false dans Amplitude depuis le
      // 04/09/2026 (HARMONIA-84).
      let hadPendingSubmission = false;
      if (status === 'completed') {
        try {
          const { useTaskSubmissionsStore } = await import('@/stores/taskSubmissions');
          hadPendingSubmission = useTaskSubmissionsStore().get(childId, date, taskId)?.status === 'pending';
        } catch {
          hadPendingSubmission = false;
        }
      }

      if (taskIndex === -1) {
        // Task doesn't exist in tasks_data, get details and add it
        try {
          const task = await taskService.getTaskById(taskId);

          // If we have a task, add it to the tasks_data
          if (task) {
            tasksData.push({
              id: task.id,
              task_id: task.id, // Add both id and task_id for compatibility
              title: task.title,
              type: task.type ?? 'recurring',
              points: task.points || 1,
              status: status,
              icon: task.icon || undefined,
              description: descriptionOf(task),
            });
          } else {
            // If task is null, create a temporary task
            tasksData.push({
              id: taskId,
              task_id: taskId,
              title: 'Task ' + taskId.substring(0, 8),
              type: 'recurring',
              points: 1,
              status: status
            });
          }
        } catch (taskError) {
          // If there's an error getting the task, create a temporary one
          console.error(`Error getting task ${taskId}:`, taskError);

          tasksData.push({
            id: taskId,
            task_id: taskId,
            title: 'Task ' + taskId.substring(0, 8),
            type: 'recurring',
            points: 1,
            status: status
          });
        }
      } else {
        // Update the status of the existing task
        tasksData[taskIndex].status = status;
      }

      // Groupes : re-dériver les entrées après le changement de statut du
      // membre. Le bonus transite par points_earned (trigger delta DB), donc
      // versement/reprise suivent mécaniquement le statut dérivé. On capture
      // les transitions pour les coins plus bas.
      let groupTransitions: Array<{ id: string; from: string; to: string; points: number }> = [];
      let allTasks: Task[] = [];
      try {
        allTasks = await taskService.getChildTasks(childId);
        const groupStatusBefore = new Map<string, string>(
          tasksData
            .filter((t: any) => t.type === 'group')
            .map((t: any) => [t.task_id || t.id, t.status]));
        syncGroupEntries(tasksData, allTasks, { premium: householdPremiumState() });
        groupTransitions = tasksData
          .filter((t: any) => t.type === 'group')
          .map((t: any) => ({
            id: t.task_id || t.id,
            from: groupStatusBefore.get(t.task_id || t.id) ?? 'pending',
            to: t.status as string,
            points: t.points || 0,
          }))
          .filter(tr => tr.from !== tr.to);
      } catch (groupErr) {
        console.error('[dailyCompletion.updateTaskStatus] Error syncing group entries:', groupErr);
      }

      // Recalculate statistics
      const completedTasks = tasksData.filter((t: any) => t.status === 'completed' && t.type === 'recurring').length;
      const completedBonus = tasksData.filter((t: any) => t.status === 'completed' && t.type === 'bonus').length;
      const pointsEarned = tasksData
        .filter((t: any) => t.status === 'completed')
        .reduce((sum: number, t: any) => sum + (t.points || 0), 0);

      // Update the completion in the database
      const updateData = {
        tasks_data: tasksData,
        tasks_completed: completedTasks,
        bonus_completed: completedBonus,
        points_earned: pointsEarned,
        updated_at: new Date().toISOString()
      };

      try {
        // If it's a temporary ID, don't try to update in the database
        if (!completion.id.startsWith('temp-')) {
          try {
            // First try using the safe RPC function
            const { error: rpcError } = await supabaseRpc.rpc(
              'update_daily_completion_safe',
              {
                p_completion_id: completion.id,
                p_tasks_data: tasksData,
                p_tasks_completed: completedTasks,
                p_bonus_completed: completedBonus,
                p_points_earned: pointsEarned
              }
            );

            if (rpcError) {
              console.error('Error updating daily completion with RPC:', rpcError);

              // Fall back to direct update if RPC fails
              const { error } = await supabase
                .from('daily_completions')
                .update(updateData)
                .eq('id', completion.id);

              if (error) {
                console.error('Error updating daily completion:', error);
                throw error;
              }
            }
          } catch (rpcError) {
            console.error('Exception in RPC call for updating daily completion:', rpcError);

            // Fall back to direct update
            const { error } = await supabase
              .from('daily_completions')
              .update(updateData)
              .eq('id', completion.id);

            if (error) {
              console.error('Error updating daily completion:', error);
              throw error;
            }
          }
          invalidateDailyCompletionCache(childId, date);
        }

        // Sync child_task_submissions status when parent validates/invalidates.
        // Les lignes renvoyées ne couvrent plus que les soumissions `rejected`
        // repassées en `approved` : les `pending` sont approuvées par le trigger
        // avant ce PATCH (voir hadPendingSubmission plus haut).
        let approvedSubmissions = 0;
        let changedSubmissions: any[] = [];
        try {
          if (status === 'completed') {
            const { data: approved } = await supabase
              .from('child_task_submissions' as any)
              .update({ status: 'approved' } as any)
              .eq('child_id', childId)
              .eq('task_id', taskId)
              .eq('date', date)
              // `rejected` aussi : TaskCard réécrivait la soumission en approved
              // quel que soit son statut. Ce PATCH est désormais le seul.
              .in('status', ['pending', 'rejected'])
              .select('id, child_id, task_id, date, status');
            approvedSubmissions = approved?.length ?? 0;
            changedSubmissions = (approved ?? []) as any[];
          } else if (status === 'pending' && originalTaskStatus === 'completed') {
            const { data: reopened } = await supabase
              .from('child_task_submissions' as any)
              .update({ status: 'pending' } as any)
              .eq('child_id', childId)
              .eq('task_id', taskId)
              .eq('date', date)
              .eq('status', 'approved')
              .select('id, child_id, task_id, date, status');
            changedSubmissions = (reopened ?? []) as any[];
          }
          // Le store des soumissions apprend le nouveau statut de la réponse
          // de l'écriture, sans attendre realtime ni relecture. Une carte qui
          // relisait le store entre les deux y trouvait encore « approved » et
          // se recochait toute seule après un décochage.
          if (changedSubmissions.length) {
            const { useTaskSubmissionsStore } = await import('@/stores/taskSubmissions');
            const submissionsStore = useTaskSubmissionsStore();
            for (const row of changedSubmissions) submissionsStore.hydrate(row);
          }
        } catch (subErr) {
          console.warn('[dailyCompletion.updateTaskStatus] Error syncing submission status:', subErr);
        }

        // Load the live task row once: used for the neutral check (points),
        // the coin award, and the allowance bonus credit below.
        let liveTask: { points?: number; allowance_bonus_amount?: number | null; title?: string; type?: string } | null = null;
        if (status === 'completed') {
          // Les tâches de l'enfant viennent d'être lues pour les groupes, et
          // portent ces quatre colonnes : on ne retourne en base que pour une
          // tâche absente de cette liste (désactivée entre-temps).
          liveTask = (allTasks.find((t) => t.id === taskId) as any) ?? null;
          if (!liveTask) {
            try {
              const { data } = await supabase
                .from('tasks')
                .select('points,allowance_bonus_amount,title,type')
                .eq('id', taskId)
                .maybeSingle();
              liveTask = data as any;
            } catch {
              liveTask = null;
            }
          }
        }

        // Points de la tâche pour l'analytique (la règle « tâche neutre = ni XP
        // ni pièce » est appliquée en base, dans le trigger, avec le même ordre
        // de priorité : définition vivante, puis instantané, puis 1).
        const taskPoints = Number(liveTask?.points ?? tasksData[taskIndex]?.points ?? 1);

        // La pièce n'est PLUS versée ici : le trigger
        // trg_sync_coins_from_completion la verse (et la reprend) dans la
        // transaction qui écrit tasks_data juste au-dessus. Versée depuis le
        // client, elle disparaissait pour de bon dès que ce bloc ne finissait
        // pas — 13 foyers n'en ont jamais reçu une seule alors que leurs points
        // étaient bien crédités (TW 17712477). On ne fait plus que rafraîchir le
        // solde affiché.
        if (status === 'completed' || (status === 'pending' && originalTaskStatus === 'completed')) {
          try {
            const { useCoinsStore } = await import('@/stores/coins');
            await useCoinsStore().loadBalance(childId);
          } catch (coinError) {
            console.warn('[dailyCompletion.updateTaskStatus] Coin balance refresh failed (non-critical):', coinError);
          }
        }

        // Allowance task bonus: credit if the task carries a money bonus.
        // Independent of the neutral (XP/coin) flag — real money, different intent.
        // Frozen at validation — the RPC is on-conflict-do-nothing, so re-validating or
        // later editing the bonus amount does NOT restate this credit. Intended.
        if (status === 'completed') {
          try {
            const bonus = Number(liveTask?.allowance_bonus_amount ?? 0);
            const label = liveTask?.title ?? '';
            if (bonus > 0) {
              const { allowanceService } = await import('@/services/allowance.service');
              await allowanceService.creditTaskBonus(childId, bonus, taskId, date, label);
            }
          } catch (allowErr) {
            console.error('[dailyCompletion.updateTaskStatus] Error crediting allowance bonus:', allowErr);
          }
        }

        // Parcours d'activation : la validation parentale est le moment où les
        // points sont réellement attribués, donc l'événement d'activation qui
        // compte. Émis ici, seul chemin de validation réellement emprunté par
        // l'interface — le store tâches qui portait ces deux événements depuis
        // le début n'est appelé par aucun écran, et aucun des deux n'avait
        // jamais atteint Amplitude.
        //
        // Garde sur `originalTaskStatus` : une revalidation d'une tâche déjà
        // cochée ne doit pas regonfler le compteur.
        if (status === 'completed' && originalTaskStatus !== 'completed') {
          const taskType = liveTask?.type || tasksData[taskIndex]?.type || 'daily';
          // Le fait « la tâche a été faite » n'est compté qu'une fois : si une
          // soumission de l'enfant existait, elle a déjà été comptée au moment
          // de la soumission (mode enfant), et seule la validation part ici.
          const hadSubmission = hadPendingSubmission || approvedSubmissions > 0;
          if (!hadSubmission) {
            analytics.logTaskCompleted(taskId, taskType, taskPoints, childId, undefined, {
              surface,
              doneBy: 'parent',
            });
          }
          analytics.logTaskValidated(taskId, childId, taskPoints, {
            surface,
            hadSubmission,
          });
        }

        // Streak check: evaluate against the household qualification rule
        // (no longer requires 100% completion). Runs on every status change.
        try {
          const { evaluateStreakForToday } = await import('@/services/streak.service')
          const { useAppContextStore } = await import('@/stores/appContext')
          const appContext = useAppContextStore()
          const household = appContext.currentHousehold
          if (household?.id) {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
            await evaluateStreakForToday({
              householdId: household.id,
              childId,
              timezone,
              rule: (household.streak_rule ?? 'at_least_one') as any,
              minCount: (household as any).streak_min_count ?? 1,
            })
          }
        } catch (streakError) {
          console.error('[dailyCompletion.updateTaskStatus] Error updating streak:', streakError)
        }

        // Reprise de la pièce à la dévalidation (FR-021) : côté base elle aussi,
        // par le même trigger. Le solde affiché est rafraîchi plus haut.

        // Allowance task bonus reversal: always reverse on dévalidation, any date.
        if (status === 'pending' && originalTaskStatus === 'completed') {
          try {
            const { allowanceService } = await import('@/services/allowance.service');
            await allowanceService.reverseTaskBonus(taskId, childId, date);
          } catch (allowErr) {
            console.error('[dailyCompletion.updateTaskStatus] Error reversing allowance bonus:', allowErr);
          }
        }

        // Coins des groupes : 1 coin quand un groupe à bonus se complète, repris
        // quand il se décomplète. Traité par le même trigger que les tâches, sur
        // les mêmes transitions de tasks_data. Les points (XP) du groupe, eux,
        // sont déjà couverts par points_earned.
        if (groupTransitions.some(t => t.points > 0)) {
          try {
            const { useCoinsStore } = await import('@/stores/coins');
            // Le solde a déjà été relu plus haut, après la même écriture.
            await useCoinsStore().loadBalance(childId, { maxAgeMs: 3000 });
          } catch (coinError) {
            console.warn('[dailyCompletion.updateTaskStatus] Coin balance refresh failed (non-critical):', coinError);
          }
        }

        // Update child points after task status change
        try {
          // First update the child's points in the database
          const { error: updateError } = await supabaseRpc.rpc('update_child_points', {
            p_child_id: childId
          });

          if (updateError) {
            console.error('Error updating child points:', updateError);
          } else {
            // Then get the updated points and update the store
            await this.updateChildPointsAndRewards(childId);
            // console.log('✅ [DailyCompletion] Child points updated');
          }
        } catch (updateError) {
          console.error('Exception updating child points:', updateError);
        }
      } catch (error) {
        console.error('Error updating task status:', error);
        // Continue despite the error to avoid blocking the user
      }
    } catch (error) {
      console.error('Error in updateTaskStatus:', error);
      throw error;
    }
  },

  // completed_tasks table removed - all task completion data now stored in daily_completions.tasks_data

  // Mark a task as completed for a day
  async markTaskCompleted(childId: string, taskId: string, date: string): Promise<void> {
    return this.updateTaskStatus(childId, date, taskId, 'completed');
  },

  // Cancel the completion of a task
  async unmarkTaskCompleted(childId: string, taskId: string, date: string): Promise<void> {
    return this.updateTaskStatus(childId, date, taskId, 'pending');
  },

  // Remove all task completions for a child
  async removeAllTaskCompletionsForChild(childId: string): Promise<void> {
    try {
      // Since task_completions table doesn't exist, we need to clear the tasks_data in daily_completions
      // This is handled by the daily completion system, so we'll just log this
      // console.log('Task completions are managed through daily_completions table');

      // Remove all daily completions for this child
      const { error: dailyError } = await supabase
        .from('daily_completions')
        .delete()
        .eq('child_id', childId);

      if (dailyError && dailyError.code !== '42501') {
        console.error('Error removing daily completions:', dailyError);
      }
    } catch (error) {
      console.error('Error in removeAllTaskCompletionsForChild:', error);
    }
  },

  // Get a single daily completion for a specific date
  async getDailyCompletion(childId: string, date: string): Promise<any> {
    try {
      try {
        // First try using RPC to avoid ambiguous column references
        const { data: rpcData, error: rpcError } = await supabaseRpc.rpc(
          'get_daily_completion',
          {
            p_child_id: childId,
            p_date: date
          }
        );

        if (!rpcError && rpcData && rpcData.length > 0) {
          const rpcCompletion = rpcData[0];
          
          // Vérifier et corriger le tasks_total si nécessaire
          const tasks = await taskService.getChildTasks(childId);
          const weeklyDone = await getWeeklyDoneCounts(childId, date, tasks);
          const availableTasks = tasks.filter(t => isTaskAvailableForDate(t, date, childId, weeklyDone));
          const correctTasksTotal = availableTasks.length;
          const correctPointsPossible = availableTasks.reduce((sum, task) => sum + (task.points || 0), 0)
            + groupPointsOf(rpcCompletion.tasks_data);

          // Si le tasks_total est incorrect, le mettre à jour
          if (rpcCompletion.tasks_total !== correctTasksTotal || rpcCompletion.points_possible !== correctPointsPossible) {
            const updatedCompletion = {
              ...rpcCompletion,
              tasks_total: correctTasksTotal,
              points_possible: correctPointsPossible,
              updated_at: new Date().toISOString()
            };

            // Sauvegarder la mise à jour si ce n'est pas un ID temporaire
            if (!rpcCompletion.id.startsWith('temp-')) {
              try {
                const { error: updateError } = await supabase
                  .from('daily_completions')
                  .update({
                    tasks_total: correctTasksTotal,
                    points_possible: correctPointsPossible,
                    updated_at: new Date().toISOString()
                  })
                  .eq('id', rpcCompletion.id);

                if (updateError) {
                  console.error('Error updating tasks_total in getDailyCompletion (RPC):', updateError);
                }
              } catch (updateErr) {
                console.error('Exception updating tasks_total in getDailyCompletion (RPC):', updateErr);
              }
            }

            return updatedCompletion;
          }

          return rpcCompletion;
        }

        // Si RPC retourne un tableau vide ou une erreur PGRST116, retourner null
        if (rpcError && rpcError.code === 'PGRST116') {
          return null;
        }

        // Si RPC retourne un tableau vide sans erreur, retourner null
        if (!rpcError && (!rpcData || rpcData.length === 0)) {
          return null;
        }

        if (rpcError && rpcError.message && !rpcError.message.includes('function does not exist') && rpcError.code !== 'PGRST116') {
          console.error('RPC error:', rpcError);
        }

        // If RPC doesn't exist or fails, fall back to direct query
      } catch (rpcError) {
        console.error('Error trying RPC method:', rpcError);
        // Continue to direct query fallback
      }

      // Direct query fallback
      // Use explicit column selection to avoid ambiguity.
      // maybeSingle: 0 ligne est un cas normal (pas encore de completion ce jour).
      const { data, error } = await supabase
        .from('daily_completions')
        .select('id, child_id, date, tasks_total, tasks_completed, bonus_total, bonus_completed, points_possible, points_earned, tasks_data, created_at, updated_at')
        .eq('child_id', childId)
        .eq('date', date)
        .maybeSingle();

      if (error) {
        // If we still get an ambiguous column error, create a temporary object
        if (error.code === '42702' && error.message.includes('ambiguous')) {
          return {
            id: 'temp-' + Date.now(),
            child_id: childId,
            date: date,
            tasks_total: 0,
            tasks_completed: 0,
            bonus_total: 0,
            bonus_completed: 0,
            points_possible: 0,
            points_earned: 0,
            tasks_data: [],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
        }

        // Pour les autres erreurs, les logger mais ne pas les lancer si c'est juste "no rows"
        console.error('Error fetching daily completion:', error);
        throw error;
      }

      // Si on a récupéré une completion, vérifier et corriger le tasks_total si nécessaire
      if (data) {
        // Récupérer les tâches pour recalculer le tasks_total correct
        const tasks = await taskService.getChildTasks(childId);
        const weeklyDone = await getWeeklyDoneCounts(childId, date, tasks);
        const availableTasks = tasks.filter(t => isTaskAvailableForDate(t, date, childId, weeklyDone));
        const correctTasksTotal = availableTasks.length;
        const correctPointsPossible = availableTasks.reduce((sum, task) => sum + (task.points || 0), 0)
          + groupPointsOf(data.tasks_data);

        // Si le tasks_total est incorrect, le mettre à jour
        if (data.tasks_total !== correctTasksTotal || data.points_possible !== correctPointsPossible) {
          // Mettre à jour la completion avec le bon tasks_total
          const updatedData = {
            ...data,
            tasks_total: correctTasksTotal,
            points_possible: correctPointsPossible,
            updated_at: new Date().toISOString()
          };

          // Sauvegarder la mise à jour si ce n'est pas un ID temporaire
          if (!data.id.startsWith('temp-')) {
            try {
              const { error: updateError } = await supabase
                .from('daily_completions')
                .update({
                  tasks_total: correctTasksTotal,
                  points_possible: correctPointsPossible,
                  updated_at: new Date().toISOString()
                })
                .eq('id', data.id);

              if (updateError) {
                console.error('Error updating tasks_total in getDailyCompletion:', updateError);
                // Retourner quand même la completion avec les valeurs corrigées
              }
            } catch (updateErr) {
              console.error('Exception updating tasks_total in getDailyCompletion:', updateErr);
            }
          }

          return updatedData;
        }
      }

      return data;
    } catch (error: any) {
      // Si l'erreur est PGRST116 (no rows found), retourner null
      if (error?.code === 'PGRST116') {
        return null;
      }

      console.error('Error in getDailyCompletion:', error);

      // Pour les autres erreurs, retourner null au lieu d'un objet par défaut
      // Le composant gérera l'absence de completion
      return null;
    }
  },

  // Update an existing daily completion
  async updateDailyCompletion(childId: string, date: string, updates: any): Promise<any> {
    const cacheKey = dailyCompletionCacheKey(childId, date);
    try {
      // Try using RPC first if available
      try {
        const { data: rpcData, error: rpcError } = await supabaseRpc.rpc(
          'update_daily_completion',
          {
            p_child_id: childId,
            p_date: date,
            p_updates: updates
          }
        );

        if (!rpcError && rpcData && rpcData.length > 0) {
          const result = rpcData[0];
          // Cache the written data so subsequent reads see it immediately
          dailyCompletionCache.set(cacheKey, { data: result, expiry: Date.now() + DAILY_COMPLETION_CACHE_TTL_MS });
          return result;
        }
        // RPC returned empty set — row doesn't exist in DB, fall through to direct update

        // If RPC call failed but exists, log the error
        if (rpcError && rpcError.message && !rpcError.message.includes('function does not exist')) {
          console.error('RPC error:', rpcError);
        }

        // Fall back to direct update
      } catch (rpcError) {
        console.error('Error trying RPC method:', rpcError);
        // Continue to direct update fallback
      }

      // Add updated_at to updates
      const updatesWithTimestamp = {
        ...updates,
        updated_at: new Date().toISOString()
      };

      // Use explicit column selection to avoid ambiguity.
      // maybeSingle: 0 ligne mise à jour est un cas géré (création puis retry).
      const { data, error } = await supabase
        .from('daily_completions')
        .update(updatesWithTimestamp)
        .eq('child_id', childId)
        .eq('date', date)
        .select('id, child_id, date, tasks_total, tasks_completed, bonus_total, bonus_completed, points_possible, points_earned, tasks_data, created_at, updated_at')
        .maybeSingle();

      if (error) {
        console.error('Error updating daily completion:', error);

        // If we get an ambiguous column error, try to get the current completion
        if (error.code === '42702' && error.message.includes('ambiguous')) {
          return this.getDailyCompletion(childId, date);
        }

        throw error;
      }

      if (!data) {
        // Row doesn't exist yet (e.g. temp completion) — create it then update
        const created = await this.getOrCreateDailyCompletion(childId, date);
        if (created && !created.id?.startsWith('temp-')) {
          const { data: retryData } = await supabase
            .from('daily_completions')
            .update(updatesWithTimestamp)
            .eq('child_id', childId)
            .eq('date', date)
            .select('id, child_id, date, tasks_total, tasks_completed, bonus_total, bonus_completed, points_possible, points_earned, tasks_data, created_at, updated_at')
            .maybeSingle();
          if (retryData) {
            dailyCompletionCache.set(cacheKey, { data: retryData, expiry: Date.now() + DAILY_COMPLETION_CACHE_TTL_MS });
            return retryData;
          }
        }
        throw new Error(`daily_completion introuvable après création (${childId} ${date})`);
      }

      // Cache the written data so subsequent reads see it immediately
      dailyCompletionCache.set(cacheKey, { data, expiry: Date.now() + DAILY_COMPLETION_CACHE_TTL_MS });
      return data;
    } catch (error) {
      console.error('Error in updateDailyCompletion:', error);

      // Try to get the current completion as a fallback
      try {
        return await this.getDailyCompletion(childId, date);
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);

        // Return a default object in case of any error
        return {
          id: 'temp-' + Date.now(),
          child_id: childId,
          date: date,
          ...updates,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      }
    }
  },

  // Helper to update child points and rewards
  async updateChildPointsAndRewards(childId: string): Promise<number> {
    try {
      const appContextStore = useAppContextStore();
      // Get the updated child points using RPC to avoid ambiguity
      const { data: childPoints, error: childError } = await supabaseRpc.rpc(
        'get_child_points',
        { p_child_id: childId }
      );

      if (!childError && childPoints !== null) {
        // Ciblé sur `childId` et non sur l'enfant courant : dans la vue « Tous »
        // l'enfant validé n'est pas celui que le store regarde, et les points
        // atterrissaient dans le mauvais objet (donc le coffre de palier aussi).
        appContextStore.updateChildPoints(childId, childPoints);
        // Assigner automatiquement les récompenses (one-time et récurrentes) quand les points sont atteints
        const householdId = appContextStore.currentHousehold?.id;
        if (householdId) {
          try {
            const { rewardService } = await import('@/services/reward.service');
            const assigned = await rewardService.assignMilestoneRewards(childId, householdId, childPoints);
            // Bouclage de la boucle produit : la récompense se déclenche. Un
            // événement par récompense attribuée, la RPC pouvant en franchir
            // plusieurs d'un coup quand un gros gain de points passe deux paliers.
            for (const rewardId of (assigned || {} as { unlockedRewards?: string[] }).unlockedRewards ?? []) {
              analytics.logRewardTriggered(childId, rewardId, childPoints, { surface: 'task_validation' });
            }
          } catch (assignErr) {
            console.error('[dailyCompletion] Error assigning milestone rewards:', assignErr);
          }
        }
        return childPoints;
      } else {
        console.error('Error fetching updated child points:', childError);
        return 0;
      }
    } catch (storeError) {
      console.error('Error updating store points:', storeError);
      return 0;
    }
  },

  // Complete a task
  async completeTask(taskId: string, childId: string, date: string): Promise<void> {
    try {
      const completion = await this.getOrCreateDailyCompletion(childId, date);

      if (completion && completion.tasks_data) {
        const taskIndex = completion.tasks_data.findIndex((task: any) => task.task_id === taskId);
        if (taskIndex !== -1) {
          completion.tasks_data[taskIndex].status = 'completed';

          // Update the completion in the database
          const { error } = await supabase
            .from('daily_completions')
            .update({
              tasks_completed: completion.tasks_data.filter((t: any) => t.status === 'completed').length,
              points_earned: completion.tasks_data
                .filter((t: any) => t.status === 'completed')
                .reduce((sum: number, task: any) => sum + task.points, 0),
              tasks_data: completion.tasks_data,
              updated_at: new Date().toISOString()
            })
            .eq('id', completion.id);

          if (error) {
            console.error('Error updating task completion:', error);
            throw error;
          }

          // Pièce (FR-001) : versée par le trigger sur l'écriture ci-dessus.

          // Streak check: evaluate against the household qualification rule.
          try {
            const { evaluateStreakForToday } = await import('@/services/streak.service')
            const { useAppContextStore } = await import('@/stores/appContext')
            const appContext = useAppContextStore()
            const household = appContext.currentHousehold
            if (household?.id) {
              const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
              await evaluateStreakForToday({
                householdId: household.id,
                childId,
                timezone,
                rule: (household.streak_rule ?? 'at_least_one') as any,
                minCount: (household as any).streak_min_count ?? 1,
              })
            }
          } catch (streakError) {
            // Non-blocking: streak tracking failure should not break task completion
            console.error('[dailyCompletion] Error updating streak:', streakError)
          }
        }
      }
    } catch (error) {
      console.error('Error completing task:', error);
      throw error;
    }
  },

  // Uncomplete a task
  async uncompleteTask(taskId: string, childId: string, date: string): Promise<void> {
    try {
      const completion = await this.getOrCreateDailyCompletion(childId, date);

      if (completion && completion.tasks_data) {
        const taskIndex = completion.tasks_data.findIndex((task: any) => task.task_id === taskId);
        if (taskIndex !== -1) {
          completion.tasks_data[taskIndex].status = 'pending';

          // Update the completion in the database
          const { error } = await supabase
            .from('daily_completions')
            .update({
              tasks_completed: completion.tasks_data.filter((t: any) => t.status === 'completed').length,
              points_earned: completion.tasks_data
                .filter((t: any) => t.status === 'completed')
                .reduce((sum: number, task: any) => sum + task.points, 0),
              tasks_data: completion.tasks_data,
              updated_at: new Date().toISOString()
            })
            .eq('id', completion.id);

          if (error) {
            console.error('Error updating task uncompletion:', error);
            throw error;
          }

          // Pièce reprise (FR-021) : par le trigger, sur l'écriture ci-dessus.
        }
      }
    } catch (error) {
      console.error('Error uncompleting task:', error);
      throw error;
    }
  }
};